namespace Mos {
  export class AcquisitionList implements IAcquisitionList {
    acquisitions!: Acquisition[];
    syncKey!: string;
    nextInterval!: number;

    constructor(data?: IAcquisitionList) {
      if (data) {
        for (var property in data) {
          if (data.hasOwnProperty(property)) (<any>this)[property] = (<any>data)[property];
        }
      }
      if (!data) {
        this.acquisitions = [];
      }
    }

    init(_data?: any) {
      if (_data) {
        if (Array.isArray(_data["acquisitions"])) {
          this.acquisitions = [] as any;
          for (let item of _data["acquisitions"]) this.acquisitions!.push(Acquisition.fromJS(item));
        }
        this.syncKey = _data["syncKey"];
        this.nextInterval = _data["nextInterval"];
      }
    }

    static fromJS(data: any): AcquisitionList {
      data = typeof data === "object" ? data : {};
      let result = new AcquisitionList();
      result.init(data);
      return result;
    }

    push(data: AcquisitionList): void {
      this.acquisitions.push(...data.acquisitions);
      this.syncKey = data.syncKey;
      this.nextInterval = data.nextInterval;
    }

    toJSON(data?: any) {
      data = typeof data === "object" ? data : {};
      if (Array.isArray(this.acquisitions)) {
        data["acquisitions"] = [];
        for (let item of this.acquisitions) data["acquisitions"].push(item.toJSON());
      }
      data["syncKey"] = this.syncKey;
      data["nextInterval"] = this.nextInterval;
      return data;
    }
  }

  export interface IAcquisitionList {
    acquisitions: Acquisition[];
    syncKey: string;
    nextInterval: number;
  }

  export class Acquisition implements IAcquisition {
    titleId!: string;
    assetId: string;
    manifestId: string;
    titleDefinition!: TitleDefinition;
    acquisitionState!: string;
    acquisitionContext!: string;
    canBeUnacquired: boolean;

    static getStoreType(acquisition: Mos.Acquisition): OSF.StoreType | undefined {
      if (acquisition?.acquisitionContext && acquisition?.titleDefinition?.scope) {
        const scope = acquisition.titleDefinition.scope.toLowerCase();
        const context = acquisition.acquisitionContext.toLowerCase();
        if (context === "user" && scope === "public") {
          return OSF.StoreType.OMEX;
        } else if (context === "tenant") {
          return OSF.StoreType.PrivateCatalog;
        } else if (context === "user") {
          return OSF.StoreType.MosExtra;
        }
      }
      return undefined;
    }

    // Currently only 'Acquired' and 'NeedsConsentForUpgrade' are expected to be kept
    // TODO: Will revisit this to determine if there are other states that need to be allowed
    static getAppState(acquisition: Mos.Acquisition): OSF.OmexState {
      switch (acquisition?.acquisitionState?.toLowerCase()) {
        case OSF.AcquisitionAcquiredState.Acquired:
        case OSF.AcquisitionAcquiredState.NeedsConsentForUpgrade:
          return OSF.OmexState.OK;
        case OSF.AcquisitionAcquiredState.Blocked:
          return OSF.OmexState.Killed;
        case OSF.AcquisitionAcquiredState.EndOfLife:
          return OSF.OmexState.Withdrawn;
        default:
          break;
      }
      return null;
    }

    static getClientAppStatus(acquisition: Mos.Acquisition): OSF.OmexClientAppStatus {
      switch (acquisition?.acquisitionState?.toLowerCase()) {
        case OSF.AcquisitionAcquiredState.NeedsConsentForUpgrade:
          // only return VersionMismatch if the app is an xml add-in
          // json add-ins allow to use none up-to-date version
          if (acquisition.titleDefinition.elementDefinitions.officeAddIns?.length > 0) {
            return OSF.OmexClientAppStatus.VersionMismatch;
          }
        case OSF.AcquisitionAcquiredState.Acquired:
          return OSF.OmexClientAppStatus.OK;
        case OSF.AcquisitionAcquiredState.Invalid:
          return OSF.OmexClientAppStatus.Invalid;
        case OSF.AcquisitionAcquiredState.Blocked:
        case OSF.AcquisitionAcquiredState.EndOfLife:
        case OSF.AcquisitionAcquiredState.Unacquired:
          Telemetry.RuntimeTelemetryHelper.LogExceptionTag(
            "ClientAppStatus should not hit this case: " + acquisition?.acquisitionState,
            null,
            null,
            0x1e34f0e3 /* tag_4npd9 */,
          );
          break;
        default:
          break;
      }
      return null;
    }

    constructor(data?: IAcquisition) {
      if (data) {
        for (var property in data) {
          if (data.hasOwnProperty(property)) (<any>this)[property] = (<any>data)[property];
        }
      }
      if (!data) {
        this.titleDefinition = new TitleDefinition();
      }
    }

    init(_data?: any) {
      if (_data) {
        this.titleId = _data["titleId"];
        this.assetId = _data["assetId"];
        this.manifestId = _data["manifestId"];
        this.titleDefinition = _data["titleDefinition"]
          ? TitleDefinition.fromJS(_data["titleDefinition"])
          : new TitleDefinition();
        this.acquisitionState = _data["acquisitionState"];
        this.acquisitionContext = _data["acquisitionContext"];
        this.canBeUnacquired = _data["canBeUnacquired"];
      }
    }

    static fromJS(data: any): Acquisition {
      data = typeof data === "object" ? data : {};
      let result = new Acquisition();
      result.init(data);
      return result;
    }

    toJSON(data?: any) {
      data = typeof data === "object" ? data : {};
      data["titleId"] = this.titleId;
      data["assetId"] = this.assetId;
      data["manifestId"] = this.manifestId;
      data["titleDefinition"] = this.titleDefinition ? this.titleDefinition.toJSON() : <any>undefined;
      data["acquisitionState"] = this.acquisitionState;
      data["acquisitionContext"] = this.acquisitionContext;
      data["canBeUnacquired"] = this.canBeUnacquired;
      return data;
    }
  }

  export interface IAcquisition {
    /** The unique ID MOS3 uses to identify each TitleData. */
    titleId: string;
    /** The unique ID Omex uses to identify each public app. All public apps, whether it is MOS app or XML add-in, have an AssetId. */
    assetId: string;
    /** The unique ID inside the manifest definition, whether it is MOS app or XML add-in. */
    manifestId: string;
    titleDefinition: TitleDefinition;
    acquisitionState: string;
    acquisitionContext: string;
    canBeUnacquired?: boolean;
  }

  export class TitleDefinition implements ITitleDefinition {
    name!: string;
    iconSmall!: IconSmall;
    version!: string;
    elementDefinitions!: ElementDefinitions;
    ingestionId!: string;
    ingestionSource!: string;
    scope!: string;
    developerName!: string;
    developerUrl: string;
    shortDescription!: string;
    /** A list of valid domains from which the tabs expect to load any content. Domain listings can include wildcards, for example `*.example.com`. If your tab configuration or content UI needs to navigate to any other domain besides the one use for tab configuration, that domain must be specified here. */
    validDomains!: string[];
    /** Specify your AAD App ID and Graph information to help users seamlessly sign into your AAD app. */
    webApplicationInfo!: WebApplicationInfo;
    /** Specify and consolidates authorization related information for the App. */
    authorization!: Authorization;

    constructor(data?: ITitleDefinition) {
      if (data) {
        for (var property in data) {
          if (data.hasOwnProperty(property)) (<any>this)[property] = (<any>data)[property];
        }
      }
      if (!data) {
        this.iconSmall = new IconSmall();
        this.elementDefinitions = new ElementDefinitions();
      }
    }

    init(_data?: any) {
      if (_data) {
        this.name = _data["name"];
        this.iconSmall = _data["iconSmall"] ? IconSmall.fromJS(_data["iconSmall"]) : new IconSmall();
        this.version = _data["version"];
        this.elementDefinitions = _data["elementDefinitions"]
          ? ElementDefinitions.fromJS(_data["elementDefinitions"])
          : new ElementDefinitions();
        this.ingestionId = _data["ingestionId"];
        this.ingestionSource = _data["ingestionSource"];
        this.scope = _data["scope"];
        this.developerName = _data["developerName"];
        this.developerUrl = _data["developerUrl"];
        this.shortDescription = _data["shortDescription"];
        if (Array.isArray(_data["validDomains"])) {
          this.validDomains = [] as any;
          for (let item of _data["validDomains"]) this.validDomains!.push(item);
        }
        this.webApplicationInfo = _data["webApplicationInfo"]
          ? WebApplicationInfo.fromJS(_data["webApplicationInfo"])
          : <any>undefined;
        this.authorization = _data["authorization"] ? Authorization.fromJS(_data["authorization"]) : <any>undefined;
      }
    }

    static fromJS(data: any): TitleDefinition {
      data = typeof data === "object" ? data : {};
      let result = new TitleDefinition();
      result.init(data);
      return result;
    }

    toJSON(data?: any) {
      data = typeof data === "object" ? data : {};
      data["name"] = this.name;
      data["iconSmall"] = this.iconSmall ? this.iconSmall.toJSON() : <any>undefined;
      data["version"] = this.version;
      data["elementDefinitions"] = this.elementDefinitions ? this.elementDefinitions.toJSON() : <any>undefined;
      data["ingestionId"] = this.ingestionId;
      data["ingestionSource"] = this.ingestionSource;
      data["scope"] = this.scope;
      data["developerName"] = this.developerName;
      data["developerUrl"] = this.developerUrl;
      data["shortDescription"] = this.shortDescription;
      if (Array.isArray(this.validDomains)) {
        data["validDomains"] = [];
        for (let item of this.validDomains) data["validDomains"].push(item);
      }
      data["webApplicationInfo"] = this.webApplicationInfo ? this.webApplicationInfo.toJSON() : <any>undefined;
      data["authorization"] = this.authorization ? this.authorization.toJSON() : <any>undefined;
      return data;
    }
  }

  export interface ITitleDefinition {
    name: string;
    iconSmall: IconSmall;
    version: string;
    elementDefinitions: ElementDefinitions;
    /** IngestionId == AssetId for all public apps (XML add-ins or MOS apps) received from Omex.
     *  IngestionId == AppId for all public apps received from Teams that are not yet published to Omex. This should be rare.
     * 	IngestionId == ManifestId for all custom apps
     */
    ingestionId: string;
    /** The source that the title originated from. */
    ingestionSource: string;
    scope: string;
    developerName: string;
    developerUrl: string;
    shortDescription: string;
    /** A list of valid domains from which the tabs expect to load any content. Domain listings can include wildcards, for example `*.example.com`. If your tab configuration or content UI needs to navigate to any other domain besides the one use for tab configuration, that domain must be specified here. */
    validDomains: string[];
    /** Specify your AAD App ID and Graph information to help users seamlessly sign into your AAD app. */
    webApplicationInfo: WebApplicationInfo;
    /** Specify and consolidates authorization related information for the App. */
    authorization: Authorization;
  }

  export class IconSmall implements IIconSmall {
    uri!: string;

    constructor(data?: IIconSmall) {
      if (data) {
        for (var property in data) {
          if (data.hasOwnProperty(property)) (<any>this)[property] = (<any>data)[property];
        }
      }
    }

    init(_data?: any) {
      if (_data) {
        this.uri = _data["uri"];
      }
    }

    static fromJS(data: any): IconSmall {
      data = typeof data === "object" ? data : {};
      let result = new IconSmall();
      result.init(data);
      return result;
    }

    toJSON(data?: any) {
      data = typeof data === "object" ? data : {};
      data["uri"] = this.uri;
      return data;
    }
  }

  export interface IIconSmall {
    uri: string;
  }

  export class ElementDefinitions implements IElementDefinitions {
    officeAddIns!: OfficeAddIn[];
    extensions!: Extension[];
    firstPartyPages!: FirstPartyPages[];

    constructor(data?: IElementDefinitions) {
      if (data) {
        for (var property in data) {
          if (data.hasOwnProperty(property)) (<any>this)[property] = (<any>data)[property];
        }
      }
    }

    init(_data?: any) {
      if (_data) {
        if (Array.isArray(_data["officeAddIns"])) {
          this.officeAddIns = [] as any;
          for (let item of _data["officeAddIns"]) this.officeAddIns!.push(OfficeAddIn.fromJS(item));
        }
        if (Array.isArray(_data["extensions"])) {
          this.extensions = [] as any;
          for (let item of _data["extensions"]) this.extensions!.push(Extension.fromJS(item));
        }
        if (Array.isArray(_data["firstPartyPages"])) {
          this.firstPartyPages = [] as any;
          for (let item of _data["firstPartyPages"]) this.firstPartyPages!.push(FirstPartyPages.fromJS(item));
        }
      }
    }

    static fromJS(data: any): ElementDefinitions {
      data = typeof data === "object" ? data : {};
      let result = new ElementDefinitions();
      result.init(data);
      return result;
    }

    toJSON(data?: any) {
      data = typeof data === "object" ? data : {};
      if (Array.isArray(this.officeAddIns)) {
        data["officeAddIns"] = [];
        for (let item of this.officeAddIns) data["officeAddIns"].push(item.toJSON());
      }
      if (Array.isArray(this.extensions)) {
        data["extensions"] = [];
        for (let item of this.extensions) data["extensions"].push(item.toJSON());
      }
      if (Array.isArray(this.firstPartyPages)) {
        data["firstPartyPages"] = [];
        for (let item of this.firstPartyPages) data["firstPartyPages"].push(item.toJSON());
      }
      return data;
    }
  }

  export interface IElementDefinitions {
    officeAddIns: OfficeAddIn[];
    extensions: Extension[];
    firstPartyPages: FirstPartyPages[];
  }

  export class WebApplicationInfo implements IWebApplicationInfo {
    /** AAD application id of the app. This id must be a GUID. */
    id!: string;
    /** Resource url of app for acquiring auth token for SSO. */
    resource!: string;

    constructor(data?: IWebApplicationInfo) {
      if (data) {
        for (var property in data) {
          if (data.hasOwnProperty(property)) (<any>this)[property] = (<any>data)[property];
        }
      }
    }

    init(_data?: any) {
      if (_data) {
        this.id = _data["id"];
        this.resource = _data["resource"];
      }
    }

    static fromJS(data: any): WebApplicationInfo {
      data = typeof data === "object" ? data : {};
      let result = new WebApplicationInfo();
      result.init(data);
      return result;
    }

    toJSON(data?: any) {
      data = typeof data === "object" ? data : {};
      data["id"] = this.id;
      data["resource"] = this.resource;
      return data;
    }
  }

  export interface IWebApplicationInfo {
    /** AAD application id of the app. This id must be a GUID. */
    id: string;
    /** Resource url of app for acquiring auth token for SSO. */
    resource: string;
  }

  export class Authorization implements IAuthorization {
    /** Specify Permissions which App lists for accessing Resource. */
    permissions!: Permissions;

    constructor(data?: IAuthorization) {
      if (data) {
        for (var property in data) {
          if (data.hasOwnProperty(property)) (<any>this)[property] = (<any>data)[property];
        }
      }
    }

    init(_data?: any) {
      if (_data) {
        this.permissions = _data["permissions"] ? Permissions.fromJS(_data["permissions"]) : <any>undefined;
      }
    }

    static fromJS(data: any): Authorization {
      data = typeof data === "object" ? data : {};
      let result = new Authorization();
      result.init(data);
      return result;
    }

    toJSON(data?: any) {
      data = typeof data === "object" ? data : {};
      data["permissions"] = this.permissions ? this.permissions.toJSON() : <any>undefined;
      return data;
    }
  }

  export interface IAuthorization {
    /** Specify Permissions which App lists for accessing Resource. */
    permissions: Permissions;
  }

  export class OfficeAddIn implements IOfficeAddIn {
    xmlDefinition!: string;
    name!: string;

    constructor(data?: IOfficeAddIn) {
      if (data) {
        for (var property in data) {
          if (data.hasOwnProperty(property)) (<any>this)[property] = (<any>data)[property];
        }
      }
    }

    init(_data?: any) {
      if (_data) {
        this.xmlDefinition = _data["xmlDefinition"];
        this.name = _data["name"];
      }
    }

    static fromJS(data: any): OfficeAddIn {
      data = typeof data === "object" ? data : {};
      let result = new OfficeAddIn();
      result.init(data);
      return result;
    }

    toJSON(data?: any) {
      data = typeof data === "object" ? data : {};
      data["xmlDefinition"] = this.xmlDefinition;
      data["name"] = this.name;
      return data;
    }
  }

  export interface IOfficeAddIn {
    xmlDefinition: string;
    name: string;
  }

  export class Extension implements IExtension {
    requirements!: Requirements;
    getStartedMessages!: GetStartedMessages[];
    runtimes!: Runtimes[];
    contentRuntimes: ContentRuntimes[];
    ribbons!: Ribbons[];
    shortcuts!: Shortcuts[];
    autoRunEvents!: AutoRunEvents[];
    contextMenus!: ContextMenus[];
    alternates!: Alternates[];

    constructor(data?: IExtension) {
      if (data) {
        for (var property in data) {
          if (data.hasOwnProperty(property)) (<any>this)[property] = (<any>data)[property];
        }
      }
      if (!data) {
        this.getStartedMessages = [];
        this.runtimes = [];
        this.contentRuntimes = [];
        this.shortcuts = [];
        this.autoRunEvents = [];
      }
    }

    init(_data?: any) {
      if (_data) {
        this.requirements = _data["requirements"] ? Requirements.fromJS(_data["requirements"]) : <any>undefined;
        if (Array.isArray(_data["getStartedMessages"])) {
          this.getStartedMessages = [] as any;
          for (let item of _data["getStartedMessages"]) this.getStartedMessages!.push(GetStartedMessages.fromJS(item));
        }
        if (Array.isArray(_data["runtimes"])) {
          this.runtimes = [] as any;
          for (let item of _data["runtimes"]) this.runtimes!.push(Runtimes.fromJS(item));
        }
        if (Array.isArray(_data["contentRuntimes"])) {
          this.contentRuntimes = [] as any;
          for (let item of _data["contentRuntimes"]) this.contentRuntimes!.push(ContentRuntimes.fromJS(item));
        }
        if (Array.isArray(_data["ribbons"])) {
          this.ribbons = [] as any;
          for (let item of _data["ribbons"]) this.ribbons!.push(Ribbons.fromJS(item));
        }
        if (Array.isArray(_data["shortcuts"])) {
          this.shortcuts = [] as any;
          for (let item of _data["shortcuts"]) this.shortcuts!.push(Shortcuts.fromJS(item));
        }
        if (Array.isArray(_data["autoRunEvents"])) {
          this.autoRunEvents = [] as any;
          for (let item of _data["autoRunEvents"]) this.autoRunEvents!.push(AutoRunEvents.fromJS(item));
        }
        if (Array.isArray(_data["contextMenus"])) {
          this.contextMenus = [] as any;
          for (let item of _data["contextMenus"]) this.contextMenus!.push(ContextMenus.fromJS(item));
        }
        if (Array.isArray(_data["alternates"])) {
          this.alternates = [] as any;
          for (let item of _data["alternates"]) this.alternates!.push(Alternates.fromJS(item));
        }
      }
    }

    static fromJS(data: any): Extension {
      data = typeof data === "object" ? data : {};
      let result = new Extension();
      result.init(data);
      return result;
    }
    toJSON(data?: any) {
      data = typeof data === "object" ? data : {};
      data["requirements"] = this.requirements ? this.requirements.toJSON() : <any>undefined;
      if (Array.isArray(this.getStartedMessages)) {
        data["getStartedMessages"] = [];
        for (let item of this.getStartedMessages) data["getStartedMessages"].push(item.toJSON());
      }
      if (Array.isArray(this.runtimes)) {
        data["runtimes"] = [];
        for (let item of this.runtimes) data["runtimes"].push(item.toJSON());
      }
      if (Array.isArray(this.contentRuntimes)) {
        data["contentRuntimes"] = [];
        for (let item of this.contentRuntimes) data["contentRuntimes"].push(item.toJSON());
      }
      if (Array.isArray(this.ribbons)) {
        data["ribbons"] = [];
        for (let item of this.ribbons) data["ribbons"].push(item.toJSON());
      }
      if (Array.isArray(this.shortcuts)) {
        data["shortcuts"] = [];
        for (let item of this.shortcuts) data["shortcuts"].push(item.toJSON());
      }
      if (Array.isArray(this.autoRunEvents)) {
        data["autoRunEvents"] = [];
        for (let item of this.autoRunEvents) data["autoRunEvents"].push(item.toJSON());
      }
      if (Array.isArray(this.contextMenus)) {
        data["contextMenus"] = [];
        for (let item of this.contextMenus) data["contextMenus"].push(item.toJSON());
      }
      if (Array.isArray(this.alternates)) {
        data["alternates"] = [];
        for (let item of this.alternates) data["alternates"].push(item.toJSON());
      }
      return data;
    }
  }

  export interface IExtension {
    requirements: Requirements;
    getStartedMessages: GetStartedMessages[];
    runtimes: Runtimes[];
    contentRuntimes: ContentRuntimes[];
    ribbons: Ribbons[];
    shortcuts: Shortcuts[];
    autoRunEvents: AutoRunEvents[];
    contextMenus: ContextMenus[];
    alternates: Alternates[];
  }

  export class FirstPartyPages implements IFirstPartyPages {
    id!: string;
    title!: string;
    subtitle!: Subtitle | undefined;
    alternateNames!: AlternateNames | undefined;
    brandBarText!: BrandBarText | undefined;
    ariaLabel!: string;
    publisher!: string;
    iconAnonymousUrl!: string;
    targetWindow!: TargetWindow | undefined;
    backgroundColor!: string;
    launchFullUrl!: string;
    name!: string;

    constructor(data?: IFirstPartyPages) {
      if (data) {
        for (var property in data) {
          if (data.hasOwnProperty(property)) (<any>this)[property] = (<any>data)[property];
        }
      }
    }

    init(_data?: any) {
      if (_data) {
        this.id = _data["Id"];
        this.title = _data["Title"];
        this.subtitle = _data["Subtitle"];
        this.alternateNames = _data["AlternateNames"];
        this.brandBarText = _data["BrandBarText"];
        this.ariaLabel = _data["AriaLabel"];
        this.publisher = _data["Publisher"];
        this.iconAnonymousUrl = _data["IconAnonymousUrl"];
        this.targetWindow = _data["TargetWindow"];
        this.backgroundColor = _data["BackgroundColor"];
        this.launchFullUrl = _data["LaunchFullUrl"];
        this.name = _data["name"];
      }
    }

    static fromJS(data: any): FirstPartyPages {
      data = typeof data === "object" ? data : {};
      let result = new FirstPartyPages();
      result.init(data);
      return result;
    }

    toJSON(data?: any) {
      data = typeof data === "object" ? data : {};
      data["Id"] = this.id;
      data["Title"] = this.title;
      data["Subtitle"] = this.subtitle;
      data["AlternateNames"] = this.alternateNames;
      data["BrandBarText"] = this.brandBarText;
      data["AriaLabel"] = this.ariaLabel;
      data["Publisher"] = this.publisher;
      data["IconAnonymousUrl"] = this.iconAnonymousUrl;
      data["TargetWindow"] = this.targetWindow;
      data["BackgroundColor"] = this.backgroundColor;
      data["LaunchFullUrl"] = this.launchFullUrl;
      data["name"] = this.name;
      return data;
    }
  }

  export interface IFirstPartyPages {
    id: string;
    title: string;
    subtitle: Subtitle | undefined;
    alternateNames: AlternateNames | undefined;
    brandBarText: BrandBarText | undefined;
    ariaLabel: string;
    publisher: string;
    iconAnonymousUrl: string;
    targetWindow: TargetWindow | undefined;
    backgroundColor: string;
    launchFullUrl: string;
    name: string;
  }

  export class Permissions implements IPermissions {
    /** App's organization wide classic permissions provided by user. */
    orgWide!: OrgWide[];
    /** App's resource specific permissions provided by user. */
    resourceSpecific!: ResourceSpecific[];

    constructor(data?: IPermissions) {
      if (data) {
        for (var property in data) {
          if (data.hasOwnProperty(property)) (<any>this)[property] = (<any>data)[property];
        }
      }
    }

    init(_data?: any) {
      if (_data) {
        if (Array.isArray(_data["orgWide"])) {
          this.orgWide = [] as any;
          for (let item of _data["orgWide"]) this.orgWide!.push(OrgWide.fromJS(item));
        }
        if (Array.isArray(_data["resourceSpecific"])) {
          this.resourceSpecific = [] as any;
          for (let item of _data["resourceSpecific"]) this.resourceSpecific!.push(ResourceSpecific.fromJS(item));
        }
      }
    }

    static fromJS(data: any): Permissions {
      data = typeof data === "object" ? data : {};
      let result = new Permissions();
      result.init(data);
      return result;
    }

    toJSON(data?: any) {
      data = typeof data === "object" ? data : {};
      if (Array.isArray(this.orgWide)) {
        data["orgWide"] = [];
        for (let item of this.orgWide) data["orgWide"].push(item.toJSON());
      }
      if (Array.isArray(this.resourceSpecific)) {
        data["resourceSpecific"] = [];
        for (let item of this.resourceSpecific) data["resourceSpecific"].push(item.toJSON());
      }
      return data;
    }
  }

  export interface IPermissions {
    /** App's organization wide classic permissions provided by user. */
    orgWide: OrgWide[];
    /** App's resource specific permissions provided by user. */
    resourceSpecific: ResourceSpecific[];
  }

  export class Requirements implements IRequirements {
    scopes!: Scopes[];
    capabilities!: Capabilities[];
    platforms!: Platforms[];
    formFactors!: FormFactors[];

    constructor(data?: IRequirements) {
      if (data) {
        for (var property in data) {
          if (data.hasOwnProperty(property)) (<any>this)[property] = (<any>data)[property];
        }
      }
    }

    init(_data?: any) {
      if (_data) {
        if (Array.isArray(_data["scopes"])) {
          this.scopes = [] as any;
          for (let item of _data["scopes"]) this.scopes!.push(item);
        }
        if (Array.isArray(_data["capabilities"])) {
          this.capabilities = [] as any;
          for (let item of _data["capabilities"]) this.capabilities!.push(Capabilities.fromJS(item));
        }
        if (Array.isArray(_data["platforms"])) {
          this.platforms = [] as any;
          for (let item of _data["platforms"]) this.platforms!.push(item);
        }
        if (Array.isArray(_data["formFactors"])) {
          this.formFactors = [] as any;
          for (let item of _data["formFactors"]) this.formFactors!.push(item);
        }
      }
    }

    static fromJS(data: any): Requirements {
      data = typeof data === "object" ? data : {};
      let result = new Requirements();
      result.init(data);
      return result;
    }

    toJSON(data?: any) {
      data = typeof data === "object" ? data : {};
      if (Array.isArray(this.scopes)) {
        data["scopes"] = [];
        for (let item of this.scopes) data["scopes"].push(item);
      }
      if (Array.isArray(this.capabilities)) {
        data["capabilities"] = [];
        for (let item of this.capabilities) data["capabilities"].push(item.toJSON());
      }
      if (Array.isArray(this.platforms)) {
        data["platforms"] = [];
        for (let item of this.platforms) data["platforms"].push(item);
      }
      if (Array.isArray(this.formFactors)) {
        data["formFactors"] = [];
        for (let item of this.formFactors) data["formFactors"].push(item);
      }
      return data;
    }
  }

  export interface IRequirements {
    scopes: Scopes[];
    capabilities: Capabilities[];
    platforms: Platforms[];
    formFactors: FormFactors[];
  }

  export class GetStartedMessages implements IGetStartedMessages {
    requirements!: Requirements;
    title!: string;
    description!: string;
    learnMoreUrl!: string;

    constructor(data?: IGetStartedMessages) {
      if (data) {
        for (var property in data) {
          if (data.hasOwnProperty(property)) (<any>this)[property] = (<any>data)[property];
        }
      }
    }

    init(_data?: any) {
      if (_data) {
        this.requirements = _data["requirements"] ? Requirements.fromJS(_data["requirements"]) : <any>undefined;
        this.title = _data["title"];
        this.description = _data["description"];
        this.learnMoreUrl = _data["learnMoreUrl"];
      }
    }

    static fromJS(data: any): GetStartedMessages {
      data = typeof data === "object" ? data : {};
      let result = new GetStartedMessages();
      result.init(data);
      return result;
    }

    toJSON(data?: any) {
      data = typeof data === "object" ? data : {};
      data["requirements"] = this.requirements ? this.requirements.toJSON() : <any>undefined;
      data["title"] = this.title;
      data["description"] = this.description;
      data["learnMoreUrl"] = this.learnMoreUrl;
      return data;
    }
  }

  export interface IGetStartedMessages {
    requirements: Requirements;
    title: string;
    description: string;
    learnMoreUrl: string;
  }

  export class ContentRuntimes implements IContentRuntimes {
    requirements: Requirements;
    id!: string;
    code!: Code;
    requestedHeight: number;
    requestedWidth: number;
    disableSnapshot: boolean;

    constructor(data?: IContentRuntimes) {
      if (data) {
        for (var property in data) {
          if (data.hasOwnProperty(property)) (<any>this)[property] = (<any>data)[property];
        }
      }
      if (!data) {
        this.code = new Code();
      }
    }

    init(_data?: any) {
      if (_data) {
        this.requirements = _data["requirements"] ? Requirements.fromJS(_data["requirements"]) : <any>undefined;
        this.id = _data["id"];
        this.code = _data["code"] ? Code.fromJS(_data["code"]) : new Code();
        this.requestedHeight = _data["requestedHeight"];
        this.requestedWidth = _data["requestedWidth"];
        this.disableSnapshot = _data["disableSnapshot"];
      }
    }

    static fromJS(data: any): ContentRuntimes {
      data = typeof data === "object" ? data : {};
      let result = new ContentRuntimes();
      result.init(data);
      return result;
    }

    toJSON(data?: any) {
      data = typeof data === "object" ? data : {};
      data["requirements"] = this.requirements ? this.requirements.toJSON() : <any>undefined;
      data["id"] = this.id;
      data["code"] = this.code ? this.code.toJSON() : <any>undefined;
      data["requestedHeight"] = this.requestedHeight;
      data["requestedWidth"] = this.requestedWidth;
      data["disableSnapshot"] = this.disableSnapshot;
      return data;
    }
  }

  export interface IContentRuntimes {
    requirements: Requirements;
    id: string;
    code: Code;
    requestedHeight: number;
    requestedWidth: number;
    disableSnapshot: boolean;
  }

  export class Runtimes implements IRuntimes {
    requirements!: Requirements;
    id!: string;
    type!: string;
    code!: Code;
    lifetime!: string;
    actions!: Actions[];
    functions!: Functions[];
    functionSettings!: FunctionSettings;

    constructor(data?: IRuntimes) {
      if (data) {
        for (var property in data) {
          if (data.hasOwnProperty(property)) (<any>this)[property] = (<any>data)[property];
        }
      }
      if (!data) {
        this.code = new Code();
        this.actions = [];
        this.functions = [];
        this.functionSettings = new FunctionSettings();
      }
    }

    init(_data?: any) {
      if (_data) {
        this.requirements = _data["requirements"] ? Requirements.fromJS(_data["requirements"]) : <any>undefined;
        this.id = _data["id"];
        this.type = _data["type"];
        this.code = _data["code"] ? Code.fromJS(_data["code"]) : new Code();
        this.lifetime = _data["lifetime"];
        if (Array.isArray(_data["actions"])) {
          this.actions = [] as any;
          for (let action of _data["actions"]) this.actions!.push(Actions.fromJS(action));
        }
        if (Array.isArray(_data["functions"])) {
          this.functions = [] as any;
          for (let item of _data["functions"]) this.functions!.push(item);
        }
        this.functionSettings = _data["functionSettings"]
          ? FunctionSettings.fromJS(_data["functionSettings"])
          : new FunctionSettings();
      }
    }

    static fromJS(data: any): Runtimes {
      data = typeof data === "object" ? data : {};
      let result = new Runtimes();
      result.init(data);
      return result;
    }

    toJSON(data?: any) {
      data = typeof data === "object" ? data : {};
      data["requirements"] = this.requirements ? this.requirements.toJSON() : <any>undefined;
      data["id"] = this.id;
      data["type"] = this.type;
      data["code"] = this.code ? this.code.toJSON() : <any>undefined;
      data["lifetime"] = this.lifetime;
      if (Array.isArray(this.actions)) {
        data["actions"] = [];
        for (let action of this.actions) data["actions"].push(action.toJSON());
      }
      if (Array.isArray(this.functions)) {
        data["functions"] = [];
        for (let item of this.functions) data["functions"].push(item.toJSON());
      }
      data["functionSettings"] = this.functionSettings ? this.functionSettings.toJSON() : <any>undefined;
      return data;
    }
  }

  export interface IRuntimes {
    requirements: Requirements;
    id: string;
    type: string;
    code: Code;
    lifetime: string;
    actions: Actions[];
    functions: Functions[];
    functionSettings: FunctionSettings;
  }

  export class Ribbons implements IRibbons {
    requirements!: Requirements;
    contexts!: Contexts[];
    tabs!: Tabs[];

    constructor(data?: IRibbons) {
      if (data) {
        for (var property in data) {
          if (data.hasOwnProperty(property)) (<any>this)[property] = (<any>data)[property];
        }
      }
      if (!data) {
        this.tabs = [];
      }
    }

    init(_data?: any) {
      if (_data) {
        this.requirements = _data["requirements"] ? Requirements.fromJS(_data["requirements"]) : <any>undefined;
        if (Array.isArray(_data["contexts"])) {
          this.contexts = [] as any;
          for (let item of _data["contexts"]) this.contexts!.push(item);
        }
        if (Array.isArray(_data["tabs"])) {
          this.tabs = [] as any;
          for (let item of _data["tabs"]) this.tabs!.push(Tabs.fromJS(item));
        }
      }
    }

    static fromJS(data: any): Ribbons {
      data = typeof data === "object" ? data : {};
      let result = new Ribbons();
      result.init(data);
      return result;
    }

    toJSON(data?: any) {
      data = typeof data === "object" ? data : {};
      data["requirements"] = this.requirements ? this.requirements.toJSON() : <any>undefined;
      if (Array.isArray(this.contexts)) {
        data["contexts"] = [];
        for (let item of this.contexts) data["contexts"].push(item);
      }
      if (Array.isArray(this.tabs)) {
        data["tabs"] = [];
        for (let item of this.tabs) data["tabs"].push(item.toJSON());
      }
      return data;
    }
  }

  export interface IRibbons {
    requirements: Requirements;
    contexts: Contexts[];
    tabs: Tabs[];
  }

  export class Shortcuts implements IShortcuts {
    requirements!: Requirements;
    keys!: Keys[];

    constructor(data?: IShortcuts) {
      if (data) {
        for (var property in data) {
          if (data.hasOwnProperty(property)) (<any>this)[property] = (<any>data)[property];
        }
      }
      if (!data) {
        this.keys = [];
      }
    }

    init(_data?: any) {
      if (_data) {
        this.requirements = _data["requirements"] ? Requirements.fromJS(_data["requirements"]) : <any>undefined;
        if (Array.isArray(_data["keys"])) {
          this.keys = [] as any;
          for (let item of _data["keys"]) this.keys!.push(Keys.fromJS(item));
        }
      }
    }

    static fromJS(data: any): Shortcuts {
      data = typeof data === "object" ? data : {};
      let result = new Shortcuts();
      result.init(data);
      return result;
    }

    toJSON(data?: any) {
      data = typeof data === "object" ? data : {};
      data["requirements"] = this.requirements ? this.requirements.toJSON() : <any>undefined;
      if (Array.isArray(this.keys)) {
        data["shortcuts"] = [];
        for (let item of this.keys) data["shortcuts"].push(item.toJSON());
      }
      return data;
    }
  }

  export interface IShortcuts {
    requirements: Requirements;
    keys: Keys[];
  }

  export class AutoRunEvents implements IAutoRunEvents {
    requirements!: Requirements;
    events!: Events[];

    constructor(data?: IAutoRunEvents) {
      if (data) {
        for (var property in data) {
          if (data.hasOwnProperty(property)) (<any>this)[property] = (<any>data)[property];
        }
      }
      if (!data) {
        this.events = [];
      }
    }

    init(_data?: any) {
      if (_data) {
        this.requirements = _data["requirements"] ? Requirements.fromJS(_data["requirements"]) : <any>undefined;
        if (Array.isArray(_data["events"])) {
          this.events = [] as any;
          for (let item of _data["events"]) this.events!.push(Events.fromJS(item));
        }
      }
    }

    static fromJS(data: any): AutoRunEvents {
      data = typeof data === "object" ? data : {};
      let result = new AutoRunEvents();
      result.init(data);
      return result;
    }

    toJSON(data?: any) {
      data = typeof data === "object" ? data : {};
      data["requirements"] = this.requirements ? this.requirements.toJSON() : <any>undefined;
      if (Array.isArray(this.events)) {
        data["events"] = [];
        for (let item of this.events) data["events"].push(item.toJSON());
      }
      return data;
    }
  }

  export interface IAutoRunEvents {
    requirements: Requirements;
    events: Events[];
  }

  export class ContextMenus implements IContextMenus {
    requirements!: Requirements;
    menus!: Menus[];

    constructor(data?: IContextMenus) {
      if (data) {
        for (var property in data) {
          if (data.hasOwnProperty(property)) (<any>this)[property] = (<any>data)[property];
        }
      }
      if (!data) {
        this.menus = [];
      }
    }

    init(_data?: any) {
      if (_data) {
        this.requirements = _data["requirements"] ? Requirements.fromJS(_data["requirements"]) : <any>undefined;
        if (Array.isArray(_data["menus"])) {
          this.menus = [] as any;
          for (let item of _data["menus"]) this.menus!.push(Menus.fromJS(item));
        }
      }
    }

    static fromJS(data: any): ContextMenus {
      data = typeof data === "object" ? data : {};
      let result = new ContextMenus();
      result.init(data);
      return result;
    }

    toJSON(data?: any) {
      data = typeof data === "object" ? data : {};
      data["requirements"] = this.requirements ? this.requirements.toJSON() : <any>undefined;
      if (Array.isArray(this.menus)) {
        data["menus"] = [];
        for (let item of this.menus) data["menus"].push(item.toJSON());
      }
      return data;
    }
  }

  export interface IContextMenus {
    requirements: Requirements;
    menus: Menus[];
  }

  export class Alternates implements IAlternates {
    requirements!: Requirements;
    prefer: Prefer;
    hide: any;
    alternateIcons: AlternateIcons;

    constructor(data?: IAlternates) {
      if (data) {
        for (var property in data) {
          if (data.hasOwnProperty(property)) (<any>this)[property] = (<any>data)[property];
        }
      }
    }

    init(_data?: any) {
      if (_data) {
        this.requirements = _data["requirements"] ? Requirements.fromJS(_data["requirements"]) : <any>undefined;
        this.prefer = _data["prefer"] ? Prefer.fromJS(_data["prefer"]) : <any>undefined;
        this.hide = _data["hide"];
        this.alternateIcons = _data["alternateIcons"] ? AlternateIcons.fromJS(_data["alternateIcons"]) : <any>undefined;
      }
    }

    static fromJS(data: any): Alternates {
      data = typeof data === "object" ? data : {};
      let result = new Alternates();
      result.init(data);
      return result;
    }

    toJSON(data?: any) {
      data = typeof data === "object" ? data : {};
      data["requirements"] = this.requirements ? this.requirements.toJSON() : <any>undefined;
      data["prefer"] = this.prefer ? this.prefer.toJSON() : <any>undefined;
      data["hide"] = this.hide;
      data["alternateIcons"] = this.alternateIcons ? this.alternateIcons.toJSON() : <any>undefined;
      return data;
    }
  }

  export interface IAlternates {
    requirements: Requirements;
    prefer: Prefer;
    hide: any;
    alternateIcons: AlternateIcons;
  }

  export class Subtitle implements ISubtitle {
    constructor(data?: ISubtitle) {
      if (data) {
        for (var property in data) {
          if (data.hasOwnProperty(property)) (<any>this)[property] = (<any>data)[property];
        }
      }
    }

    init(_data?: any) {}

    static fromJS(data: any): Subtitle {
      data = typeof data === "object" ? data : {};
      let result = new Subtitle();
      result.init(data);
      return result;
    }

    toJSON(data?: any) {
      data = typeof data === "object" ? data : {};
      return data;
    }
  }

  export interface ISubtitle {}

  export class AlternateNames implements IAlternateNames {
    constructor(data?: IAlternateNames) {
      if (data) {
        for (var property in data) {
          if (data.hasOwnProperty(property)) (<any>this)[property] = (<any>data)[property];
        }
      }
    }

    init(_data?: any) {}

    static fromJS(data: any): AlternateNames {
      data = typeof data === "object" ? data : {};
      let result = new AlternateNames();
      result.init(data);
      return result;
    }

    toJSON(data?: any) {
      data = typeof data === "object" ? data : {};
      return data;
    }
  }

  export interface IAlternateNames {}

  export class BrandBarText implements IBrandBarText {
    constructor(data?: IBrandBarText) {
      if (data) {
        for (var property in data) {
          if (data.hasOwnProperty(property)) (<any>this)[property] = (<any>data)[property];
        }
      }
    }

    init(_data?: any) {}

    static fromJS(data: any): BrandBarText {
      data = typeof data === "object" ? data : {};
      let result = new BrandBarText();
      result.init(data);
      return result;
    }

    toJSON(data?: any) {
      data = typeof data === "object" ? data : {};
      return data;
    }
  }

  export interface IBrandBarText {}

  export class TargetWindow implements ITargetWindow {
    constructor(data?: ITargetWindow) {
      if (data) {
        for (var property in data) {
          if (data.hasOwnProperty(property)) (<any>this)[property] = (<any>data)[property];
        }
      }
    }

    init(_data?: any) {}

    static fromJS(data: any): TargetWindow {
      data = typeof data === "object" ? data : {};
      let result = new TargetWindow();
      result.init(data);
      return result;
    }

    toJSON(data?: any) {
      data = typeof data === "object" ? data : {};
      return data;
    }
  }

  export interface ITargetWindow {}

  export class OrgWide implements IOrgWide {
    /** The Permission name of app's permission node. */
    name!: string;
    /** The Permission type of app's permission node. */
    type!: OrgWideType;

    constructor(data?: IOrgWide) {
      if (data) {
        for (var property in data) {
          if (data.hasOwnProperty(property)) (<any>this)[property] = (<any>data)[property];
        }
      }
    }

    init(_data?: any) {
      if (_data) {
        this.name = _data["name"];
        this.type = _data["type"];
      }
    }

    static fromJS(data: any): OrgWide {
      data = typeof data === "object" ? data : {};
      let result = new OrgWide();
      result.init(data);
      return result;
    }

    toJSON(data?: any) {
      data = typeof data === "object" ? data : {};
      data["name"] = this.name;
      data["type"] = this.type;
      return data;
    }
  }

  export interface IOrgWide {
    /** The Permission name of app's permission node. */
    name: string;
    /** The Permission type of app's permission node. */
    type: OrgWideType;
  }

  export class ResourceSpecific implements IResourceSpecific {
    /** The Permission name of app's permission node. */
    name!: string;
    /** The Permission type of app's permission node. */
    type!: ResourceSpecificType;

    constructor(data?: IResourceSpecific) {
      if (data) {
        for (var property in data) {
          if (data.hasOwnProperty(property)) (<any>this)[property] = (<any>data)[property];
        }
      }
    }

    init(_data?: any) {
      if (_data) {
        this.name = _data["name"];
        this.type = _data["type"];
      }
    }

    static fromJS(data: any): ResourceSpecific {
      data = typeof data === "object" ? data : {};
      let result = new ResourceSpecific();
      result.init(data);
      return result;
    }

    toJSON(data?: any) {
      data = typeof data === "object" ? data : {};
      data["name"] = this.name;
      data["type"] = this.type;
      return data;
    }
  }

  export interface IResourceSpecific {
    /** The Permission name of app's permission node. */
    name: string;
    /** The Permission type of app's permission node. */
    type: ResourceSpecificType;
  }

  export enum Scopes {
    Mail = "mail",
    Workbook = "workbook",
    Document = "document",
    Presentation = "presentation",
  }

  export class Capabilities implements ICapabilities {
    name!: string;
    minVersion!: string;
    maxVersion!: string;

    constructor(data?: ICapabilities) {
      if (data) {
        for (var property in data) {
          if (data.hasOwnProperty(property)) (<any>this)[property] = (<any>data)[property];
        }
      }
    }

    init(_data?: any) {
      if (_data) {
        this.name = _data["name"];
        this.minVersion = _data["minVersion"];
        this.maxVersion = _data["maxVersion"];
      }
    }

    static fromJS(data: any): Capabilities {
      data = typeof data === "object" ? data : {};
      let result = new Capabilities();
      result.init(data);
      return result;
    }

    toJSON(data?: any) {
      data = typeof data === "object" ? data : {};
      data["name"] = this.name;
      data["minVersion"] = this.minVersion;
      data["maxVersion"] = this.maxVersion;
      return data;
    }
  }

  export interface ICapabilities {
    name: string;
    minVersion: string;
    maxVersion: string;
  }

  export enum Platforms {
    Windows = "windows",
    Mac = "mac",
    Web = "web",
    Ios = "ios",
    Android = "android",
  }

  export enum FormFactors {
    Desktop = "desktop",
    Tablet = "tablet",
    Phone = "phone",
  }

  export class Code implements ICode {
    page!: string;
    script!: string;

    constructor(data?: ICode) {
      if (data) {
        for (var property in data) {
          if (data.hasOwnProperty(property)) (<any>this)[property] = (<any>data)[property];
        }
      }
    }

    init(_data?: any) {
      if (_data) {
        this.page = _data["page"];
        this.script = _data["script"];
      }
    }

    static fromJS(data: any): Code {
      data = typeof data === "object" ? data : {};
      let result = new Code();
      result.init(data);
      return result;
    }

    toJSON(data?: any) {
      data = typeof data === "object" ? data : {};
      data["page"] = this.page;
      data["script"] = this.script;
      return data;
    }
  }

  export interface ICode {
    page: string;
    script: string;
  }

  export class Actions implements IActions {
    id!: string;
    type!: string;
    displayName!: string;
    view!: string;

    constructor(data?: IActions) {
      if (data) {
        for (var property in data) {
          if (data.hasOwnProperty(property)) (<any>this)[property] = (<any>data)[property];
        }
      }
    }

    init(_data?: any) {
      if (_data) {
        this.id = _data["id"];
        this.type = _data["type"];
        this.displayName = _data["displayName"];
        this.view = _data["view"];
      }
    }

    static fromJS(data: any): Actions {
      data = typeof data === "object" ? data : {};
      let result = new Actions();
      result.init(data);
      return result;
    }

    toJSON(data?: any) {
      data = typeof data === "object" ? data : {};
      data["id"] = this.id;
      data["type"] = this.type;
      data["displayName"] = this.displayName;
      data["view"] = this.view;
      return data;
    }
  }

  export interface IActions {
    id: string;
    type: string;
    displayName: string;
    view: string;
  }

  export class Functions implements IFunctions {
    id!: string;
    name!: string;
    description!: string;
    parameters!: Parameters[];
    result!: Result;
    stream!: boolean;
    volatile!: boolean;
    cancelable!: boolean;
    requiresAddress!: boolean;
    requiresParameterAddress!: boolean;

    constructor(data?: IFunctions) {
      if (data) {
        for (var property in data) {
          if (data.hasOwnProperty(property)) (<any>this)[property] = (<any>data)[property];
        }
      }
      if (!data) {
        this.result = new Result();
        this.parameters = [];
      }
    }

    init(_data?: any) {
      if (_data) {
        this.id = _data["id"];
        this.name = _data["name"];
        this.description = _data["description"];
        if (Array.isArray(_data["parameters"])) {
          this.parameters = [] as any;
          for (let item of _data["parameters"]) this.parameters!.push(Parameters.fromJS(item));
        }
        this.result = _data["result"] ? Result.fromJS(_data["result"]) : new Result();
        this.stream = _data["stream"];
        this.volatile = _data["volatile"];
        this.cancelable = _data["cancelable"];
        this.requiresAddress = _data["requiresAddress"];
        this.requiresParameterAddress = _data["requiresParameterAddress"];
      }
    }

    static fromJS(data: any): Functions {
      data = typeof data === "object" ? data : {};
      let result = new Functions();
      result.init(data);
      return result;
    }

    toJSON(data?: any) {
      data = typeof data === "object" ? data : {};
      data["id"] = this.id;
      data["name"] = this.name;
      data["description"] = this.description;
      if (Array.isArray(this.parameters)) {
        data["parameters"] = [];
        for (let item of this.parameters) data["parameters"].push(item.toJSON());
      }
      data["result"] = this.result ? this.result.toJSON() : <any>undefined;
      data["stream"] = this.stream;
      data["volatile"] = this.volatile;
      data["cancelable"] = this.cancelable;
      data["requiresAddress"] = this.requiresAddress;
      data["requiresParameterAddress"] = this.requiresParameterAddress;
      return data;
    }
  }

  export interface IFunctions {
    id: string;
    name: string;
    description: string;
    parameters: IParameters[];
    result: IResult;
    stream: boolean;
    volatile: boolean;
    cancelable: boolean;
    requiresAddress: boolean;
    requiresParameterAddress: boolean;
  }

  export class FunctionSettings implements IFunctionSettings {
    namespace!: Namespace;
    allowErrorForDataTypeAny!: boolean;
    allowRichDataForDataTypeAny!: boolean;

    constructor(data?: IFunctionSettings) {
      if (data) {
        for (var property in data) {
          if (data.hasOwnProperty(property)) (<any>this)[property] = (<any>data)[property];
        }
      }
      if (!data) {
        this.namespace = new Namespace();
      }
    }

    init(_data?: any) {
      if (_data) {
        this.namespace = _data["namespace"] ? Namespace.fromJS(_data["namespace"]) : new Namespace();
        this.allowErrorForDataTypeAny = _data["allowErrorForDataTypeAny"];
        this.allowRichDataForDataTypeAny = _data["allowRichDataForDataTypeAny"];
      }
    }

    static fromJS(data: any): FunctionSettings {
      data = typeof data === "object" ? data : {};
      let result = new FunctionSettings();
      result.init(data);
      return result;
    }

    toJSON(data?: any) {
      data = typeof data === "object" ? data : {};
      data["namespace"] = this.namespace ? this.namespace.toJSON() : <any>undefined;
      data["allowErrorForDataTypeAny"] = this.allowErrorForDataTypeAny;
      data["allowRichDataForDataTypeAny"] = this.allowRichDataForDataTypeAny;
      return data;
    }
  }

  export interface IFunctionSettings {
    namespace: INamespace;
    allowErrorForDataTypeAny: boolean;
    allowRichDataForDataTypeAny: boolean;
  }

  export enum Contexts {
    Default = "default",
    ReadMail = "readMail",
    ComposeMail = "composeMail",
    AppointmentOrganizer = "appointmentOrganizer",
    AppointmentAttendee = "appointmentAttendee",
  }

  export class Tabs implements ITabs {
    /** Built in tab id of built in tab. Maximum length is 64 characters. */
    builtInTabId!: string;
    /** App-wide unique ID. Maximum length is 64 characters. */
    id!: string;
    /** Short label of the tab. Maximum length is 64 characters. */
    label!: string;
    position!: Position;
    groups!: Groups[];

    constructor(data?: ITabs) {
      if (data) {
        for (var property in data) {
          if (data.hasOwnProperty(property)) (<any>this)[property] = (<any>data)[property];
        }
      }
      if (!data) {
        this.groups = [];
      }
    }

    init(_data?: any) {
      if (_data) {
        this.builtInTabId = _data["builtInTabId"];
        this.id = _data["id"];
        this.label = _data["label"];
        this.position = _data["position"] ? Position.fromJS(_data["position"]) : <any>undefined;
        if (Array.isArray(_data["groups"])) {
          this.groups = [] as any;
          for (let item of _data["groups"]) this.groups!.push(Groups.fromJS(item));
        }
      }
    }

    static fromJS(data: any): Tabs {
      data = typeof data === "object" ? data : {};
      let result = new Tabs();
      result.init(data);
      return result;
    }

    toJSON(data?: any) {
      data = typeof data === "object" ? data : {};
      data["builtInTabId"] = this.builtInTabId;
      data["id"] = this.id;
      data["label"] = this.label;
      data["position"] = this.position ? this.position.toJSON() : <any>undefined;
      if (Array.isArray(this.groups)) {
        data["groups"] = [];
        for (let item of this.groups) data["groups"].push(item.toJSON());
      }
      return data;
    }
  }

  export interface ITabs {
    /** Built in tab id of built in tab. Maximum length is 64 characters. */
    builtInTabId: string;
    /** App-wide unique ID. Maximum length is 64 characters. */
    id: string;
    /** Short label of the tab. Maximum length is 64 characters. */
    label: string;
    position: Position;
    groups: Groups[];
  }

  export class Keys implements IKeys {
    requirements!: Requirements;
    key!: Key;
    actionId!: string;

    constructor(data?: IKeys) {
      if (data) {
        for (var property in data) {
          if (data.hasOwnProperty(property)) (<any>this)[property] = (<any>data)[property];
        }
      }
    }

    init(_data?: any) {
      if (_data) {
        this.requirements = _data["requirements"] ? Requirements.fromJS(_data["requirements"]) : <any>undefined;
        this.key = _data["key"] ? Key.fromJS(_data["key"]) : <any>undefined;
        this.actionId = _data["actionId"];
      }
    }

    static fromJS(data: any): Keys {
      data = typeof data === "object" ? data : {};
      let result = new Keys();
      result.init(data);
      return result;
    }

    toJSON(data?: any) {
      data = typeof data === "object" ? data : {};
      data["requirements"] = this.requirements ? this.requirements.toJSON() : <any>undefined;
      data["key"] = this.key ? this.key.toJSON() : <any>undefined;
      data["actionId"] = this.actionId;
      return data;
    }
  }

  export interface IKeys {
    requirements: Requirements;
    key: Key;
    actionId: string;
  }

  export class Key implements IKey {
    defaultKey!: string;
    windows!: string;
    mac!: string;
    web!: string;

    constructor(data?: IKey) {
      if (data) {
        for (var property in data) {
          if (data.hasOwnProperty(property)) (<any>this)[property] = (<any>data)[property];
        }
      }
    }

    init(_data?: any) {
      if (_data) {
        this.defaultKey = _data["default"];
        this.windows = _data["windows"];
        this.mac = _data["mac"];
        this.web = _data["web"];
      }
    }

    static fromJS(data: any): Key {
      data = typeof data === "object" ? data : {};
      let result = new Key();
      result.init(data);
      return result;
    }

    toJSON(data?: any) {
      data = typeof data === "object" ? data : {};
      data["default"] = this.defaultKey;
      data["windows"] = this.windows;
      data["mac"] = this.mac;
      data["web"] = this.web;
      return data;
    }
  }

  export interface IKey {
    defaultKey: string;
    windows: string;
    mac: string;
    web: string;
  }

  export class Events implements IEvents {
    requirements!: Requirements;
    id!: string;
    actionId!: string;
    options!: Options;

    constructor(data?: IEvents) {
      if (data) {
        for (var property in data) {
          if (data.hasOwnProperty(property)) (<any>this)[property] = (<any>data)[property];
        }
      }
      if (!data) {
        this.options = new Options();
      }
    }

    init(_data?: any) {
      if (_data) {
        this.requirements = _data["requirements"] ? Requirements.fromJS(_data["requirements"]) : <any>undefined;
        this.id = _data["id"];
        this.actionId = _data["actionId"];
        this.options = _data["options"] ? Options.fromJS(_data["options"]) : new Options();
      }
    }

    static fromJS(data: any): Events {
      data = typeof data === "object" ? data : {};
      let result = new Events();
      result.init(data);
      return result;
    }

    toJSON(data?: any) {
      data = typeof data === "object" ? data : {};
      data["requirements"] = this.requirements ? this.requirements.toJSON() : <any>undefined;
      data["id"] = this.id;
      data["actionId"] = this.actionId;
      data["options"] = this.options ? this.options.toJSON() : <any>undefined;
      return data;
    }
  }

  export interface IEvents {
    requirements: Requirements;
    id: string;
    actionId: string;
    options: Options;
  }

  export class Menus implements IMenus {
    /** Use cell/text here for Office native context menu ContextMenuCell/ContextMenuText. */
    entryPoint!: MenusType;
    controls!: any[];

    constructor(data?: IMenus) {
      if (data) {
        for (var property in data) {
          if (data.hasOwnProperty(property)) (<any>this)[property] = (<any>data)[property];
        }
      }
      if (!data) {
        this.controls = [];
      }
    }

    init(_data?: any) {
      if (_data) {
        this.entryPoint = _data["entryPoint"];
        if (Array.isArray(_data["controls"])) {
          this.controls = [] as any;
          for (let item of _data["controls"]) this.controls!.push(item);
        }
      }
    }

    static fromJS(data: any): Menus {
      data = typeof data === "object" ? data : {};
      let result = new Menus();
      result.init(data);
      return result;
    }

    toJSON(data?: any) {
      data = typeof data === "object" ? data : {};
      data["entryPoint"] = this.entryPoint;
      if (Array.isArray(this.controls)) {
        data["controls"] = [];
        for (let item of this.controls) data["controls"].push(item);
      }
      return data;
    }
  }

  export interface IMenus {
    /** Use cell/text here for Office native context menu ContextMenuCell/ContextMenuText. */
    entryPoint: MenusType;
    controls: any[];
  }

  export enum OrgWideType {
    Application = "Application",
    Delegated = "Delegated",
  }

  export enum ResourceSpecificType {
    Application = "Application",
    Delegated = "Delegated",
  }

  export class Namespace implements INamespace {
    id!: string;
    name!: string;

    constructor(data?: INamespace) {
      if (data) {
        for (var property in data) {
          if (data.hasOwnProperty(property)) (<any>this)[property] = (<any>data)[property];
        }
      }
    }

    init(_data?: any) {
      if (_data) {
        this.id = _data["id"];
        this.name = _data["name"];
      }
    }

    static fromJS(data: any): Namespace {
      data = typeof data === "object" ? data : {};
      let result = new Namespace();
      result.init(data);
      return result;
    }

    toJSON(data?: any) {
      data = typeof data === "object" ? data : {};
      data["id"] = this.id;
      data["name"] = this.name;
      return data;
    }
  }

  export interface INamespace {
    id: string;
    name: string;
  }

  export class Position implements IPosition {
    /** Built in tab id of built in tab. Maximum length is 64 characters. */
    builtInTabId!: string;
    /** Define alignment of this custom tab with reference to the BuiltIn tab. */
    align!: PositionAlign;

    constructor(data?: IPosition) {
      if (data) {
        for (var property in data) {
          if (data.hasOwnProperty(property)) (<any>this)[property] = (<any>data)[property];
        }
      }
    }

    init(_data?: any) {
      if (_data) {
        this.builtInTabId = _data["builtInTabId"];
        this.align = _data["align"];
      }
    }

    static fromJS(data: any): Position {
      data = typeof data === "object" ? data : {};
      let result = new Position();
      result.init(data);
      return result;
    }

    toJSON(data?: any) {
      data = typeof data === "object" ? data : {};
      data["builtInTabId"] = this.builtInTabId;
      data["align"] = this.align;
      return data;
    }
  }

  export interface IPosition {
    /** Built in tab id of built in tab. Maximum length is 64 characters. */
    builtInTabId: string;
    /** Define alignment of this custom tab with reference to the BuiltIn tab. */
    align: PositionAlign;
  }

  export class Groups implements IGroups {
    overriddenByRibbonApi!: boolean;
    /** Built in group id of built in group. Maximum length is 64 characters. */
    builtInGroupId!: string;
    /** App-wide unique ID. Maximum length is 64 characters. */
    id!: string;
    /** Short label of the group. Maximum length is 64 characters. */
    label!: string;
    icons!: Icons[];
    controls!: Controls[];

    constructor(data?: IGroups) {
      if (data) {
        for (var property in data) {
          if (data.hasOwnProperty(property)) (<any>this)[property] = (<any>data)[property];
        }
      }
      if (!data) {
        this.overriddenByRibbonApi = false;
        this.controls = [];
      }
    }

    init(_data?: any) {
      if (_data) {
        this.overriddenByRibbonApi =
          _data["overriddenByRibbonApi"] !== undefined ? _data["overriddenByRibbonApi"] : false;
        this.builtInGroupId = _data["builtInGroupId"];
        this.id = _data["id"];
        this.label = _data["label"];
        if (Array.isArray(_data["icons"])) {
          this.icons = [] as any;
          for (let item of _data["icons"]) this.icons!.push(Icons.fromJS(item));
        }
        if (Array.isArray(_data["controls"])) {
          this.controls = [] as any;
          for (let item of _data["controls"]) this.controls!.push(Controls.fromJS(item));
        }
      }
    }

    static fromJS(data: any): Groups {
      data = typeof data === "object" ? data : {};
      let result = new Groups();
      result.init(data);
      return result;
    }

    toJSON(data?: any) {
      data = typeof data === "object" ? data : {};
      data["overriddenByRibbonApi"] = this.overriddenByRibbonApi;
      data["builtInGroupId"] = this.builtInGroupId;
      data["id"] = this.id;
      data["label"] = this.label;
      if (Array.isArray(this.icons)) {
        data["icons"] = [];
        for (let item of this.icons) data["icons"].push(item.toJSON());
      }
      if (Array.isArray(this.controls)) {
        data["controls"] = [];
        for (let item of this.controls) data["controls"].push(item.toJSON());
      }
      return data;
    }
  }

  export interface IGroups {
    overriddenByRibbonApi: boolean;
    /** Built in group id of built in group. Maximum length is 64 characters. */
    builtInGroupId: string;
    /** App-wide unique ID. Maximum length is 64 characters. */
    id: string;
    /** Short label of the group. Maximum length is 64 characters. */
    label: string;
    icons: Icons[];
    controls: Controls[];
  }

  export class Options implements IOptions {
    sendMode!: string;

    constructor(data?: IOptions) {
      if (data) {
        for (var property in data) {
          if (data.hasOwnProperty(property)) (<any>this)[property] = (<any>data)[property];
        }
      }
    }

    init(_data?: any) {
      if (_data) {
        this.sendMode = _data["sendMode"];
      }
    }

    static fromJS(data: any): Options {
      data = typeof data === "object" ? data : {};
      let result = new Options();
      result.init(data);
      return result;
    }

    toJSON(data?: any) {
      data = typeof data === "object" ? data : {};
      data["sendMode"] = this.sendMode;
      return data;
    }
  }

  export interface IOptions {
    sendMode: string;
  }

  export enum MenusType {
    Cell = "cell",
    Text = "text",
  }

  export class Parameters implements IParameters {
    name!: string;
    description!: string;
    type!: string;
    dimensionality!: string;

    constructor(data?: IParameters) {
      if (data) {
        for (var property in data) {
          if (data.hasOwnProperty(property)) (<any>this)[property] = (<any>data)[property];
        }
      }
    }

    init(_data?: any) {
      if (_data) {
        this.name = _data["name"];
        this.description = _data["description"];
        this.type = _data["type"];
        this.dimensionality = _data["dimensionality"];
      }
    }

    static fromJS(data: any): Parameters {
      data = typeof data === "object" ? data : {};
      let result = new Parameters();
      result.init(data);
      return result;
    }

    toJSON(data?: any) {
      data = typeof data === "object" ? data : {};
      data["name"] = this.name;
      data["description"] = this.description;
      data["type"] = this.type;
      data["dimensionality"] = this.dimensionality;
      return data;
    }
  }

  export interface IParameters {
    name: string;
    description: string;
    type: string;
    dimensionality: string;
  }

  export class Result implements IResult {
    dimensionality!: string;

    constructor(data?: IResult) {
      if (data) {
        for (var property in data) {
          if (data.hasOwnProperty(property)) (<any>this)[property] = (<any>data)[property];
        }
      }
    }

    init(_data?: any) {
      if (_data) {
        this.dimensionality = _data["dimensionality"];
      }
    }

    static fromJS(data: any): Result {
      data = typeof data === "object" ? data : {};
      let result = new Result();
      result.init(data);
      return result;
    }

    toJSON(data?: any) {
      data = typeof data === "object" ? data : {};
      data["dimensionality"] = this.dimensionality;
      return data;
    }
  }

  export interface IResult {
    dimensionality: string;
  }

  export enum PositionAlign {
    After = "after",
    Before = "before",
  }

  export class Icons implements IIcons {
    /** Size in pixels of the side of the icon, e.g. 32, 64, 128. */
    size!: number;
    /** Relative path that contains the icon. */
    url!: string;

    constructor(data?: IIcons) {
      if (data) {
        for (var property in data) {
          if (data.hasOwnProperty(property)) (<any>this)[property] = (<any>data)[property];
        }
      }
    }

    init(_data?: any) {
      if (_data) {
        this.size = _data["size"];
        this.url = _data["url"];
      }
    }

    static fromJS(data: any): Icons {
      data = typeof data === "object" ? data : {};
      let result = new Icons();
      result.init(data);
      return result;
    }

    toJSON(data?: any) {
      data = typeof data === "object" ? data : {};
      data["size"] = this.size;
      data["url"] = this.url;
      return data;
    }
  }

  export interface IIcons {
    /** Size in pixels of the side of the icon, e.g. 32, 64, 128. */
    size: number;
    /** Relative path that contains the icon. */
    url: string;
  }

  export class AlternateIcons implements IAlternateIcons {
    icon: Icons;
    highResolutionIcon: Icons;

    constructor(data?: IAlternateIcons) {
      if (data) {
        for (var property in data) {
          if (data.hasOwnProperty(property)) (<any>this)[property] = (<any>data)[property];
        }
      }
      if (!data) {
        this.icon = new Icons();
        this.highResolutionIcon = new Icons();
      }
    }

    init(_data?: any) {
      if (_data) {
        this.icon = _data["icon"] ? Icons.fromJS(_data["icon"]) : new Icons();
        this.highResolutionIcon = _data["highResolutionIcon"] ? Icons.fromJS(_data["highResolutionIcon"]) : new Icons();
      }
    }

    static fromJS(data: any): AlternateIcons {
      data = typeof data === "object" ? data : {};
      let result = new AlternateIcons();
      result.init(data);
      return result;
    }

    toJSON(data?: any) {
      data = typeof data === "object" ? data : {};
      data["icon"] = this.icon ? this.icon.toJSON() : <any>undefined;
      data["highResolutionIcon"] = this.highResolutionIcon ? this.highResolutionIcon.toJSON() : <any>undefined;
      return data;
    }
  }

  export interface IAlternateIcons {
    icon: Icons;
    highResolutionIcon: Icons;
  }

  export class Prefer implements IPrefer {
    comAddin: ComAddin;

    constructor(data?: IAlternates) {
      if (data) {
        for (var property in data) {
          if (data.hasOwnProperty(property)) (<any>this)[property] = (<any>data)[property];
        }
      }
      if (!data) {
        this.comAddin = new ComAddin();
      }
    }

    init(_data?: any) {
      if (_data) {
        this.comAddin = _data["comAddin"] ? ComAddin.fromJS(_data["comAddin"]) : new ComAddin();
      }
    }

    static fromJS(data: any): Prefer {
      data = typeof data === "object" ? data : {};
      let result = new Prefer();
      result.init(data);
      return result;
    }

    toJSON(data?: any) {
      data = typeof data === "object" ? data : {};
      data["comAddin"] = this.comAddin ? this.comAddin.toJSON() : <any>undefined;
      return data;
    }
  }

  export interface IPrefer {
    comAddin: ComAddin;
  }

  export class ComAddin implements IComAddin {
    progId: string;

    constructor(data?: IComAddin) {
      if (data) {
        for (var property in data) {
          if (data.hasOwnProperty(property)) (<any>this)[property] = (<any>data)[property];
        }
      }
    }

    init(_data?: any) {
      if (_data) {
        this.progId = _data["progId"];
      }
    }

    static fromJS(data: any): ComAddin {
      data = typeof data === "object" ? data : {};
      let result = new ComAddin();
      result.init(data);
      return result;
    }

    toJSON(data?: any) {
      data = typeof data === "object" ? data : {};
      data["progId"] = this.progId;
      return data;
    }
  }

  export interface IComAddin {
    progId: string;
  }

  export class Controls implements IControls {
    overriddenByRibbonApi!: boolean;
    /** Built in control id of built in control. Maximum length is 64 characters. */
    builtInControlId!: string;
    /** App-wide unique ID. Maximum length is 64 characters. */
    id!: string;
    type!: ControlsType;
    /** Short label of the control. Maximum length is 64 characters. */
    label!: string;
    /** Whether the control is initially enabled. */
    enabled!: boolean;
    icons!: Icons[];
    supertip!: Supertip;
    /** The ID of an execution-type action that handles this key combination. Maximum length is 64 characters. */
    actionId!: string;
    items!: Controls[];

    constructor(data?: IControls) {
      if (data) {
        for (var property in data) {
          if (data.hasOwnProperty(property)) (<any>this)[property] = (<any>data)[property];
        }
      }
      if (!data) {
        this.overriddenByRibbonApi = false;
        this.enabled = true;
        this.icons = [];
        this.supertip = new Supertip();
      }
    }

    init(_data?: any) {
      if (_data) {
        this.overriddenByRibbonApi =
          _data["overriddenByRibbonApi"] !== undefined ? _data["overriddenByRibbonApi"] : false;
        this.builtInControlId = _data["builtInControlId"];
        this.id = _data["id"];
        this.type = _data["type"];
        this.label = _data["label"];
        this.enabled = _data["enabled"] !== undefined ? _data["enabled"] : true;
        if (Array.isArray(_data["icons"])) {
          this.icons = [] as any;
          for (let item of _data["icons"]) this.icons!.push(Icons.fromJS(item));
        }
        this.supertip = _data["supertip"] ? Supertip.fromJS(_data["supertip"]) : new Supertip();
        this.actionId = _data["actionId"];
        if (Array.isArray(_data["items"])) {
          this.items = [] as any;
          for (let item of _data["items"]) this.items!.push(Controls.fromJS(item));
        }
      }
    }

    static fromJS(data: any): Controls {
      data = typeof data === "object" ? data : {};
      let result = new Controls();
      result.init(data);
      return result;
    }

    toJSON(data?: any) {
      data = typeof data === "object" ? data : {};
      data["overriddenByRibbonApi"] = this.overriddenByRibbonApi;
      data["builtInControlId"] = this.builtInControlId;
      data["id"] = this.id;
      data["type"] = this.type;
      data["label"] = this.label;
      data["enabled"] = this.enabled;
      if (Array.isArray(this.icons)) {
        data["icons"] = [];
        for (let item of this.icons) data["icons"].push(item.toJSON());
      }
      data["supertip"] = this.supertip ? this.supertip.toJSON() : <any>undefined;
      data["actionId"] = this.actionId;
      if (Array.isArray(this.items)) {
        data["items"] = [];
        for (let item of this.items) data["items"].push(item.toJSON());
      }
      return data;
    }
  }

  export interface IControls {
    overriddenByRibbonApi: boolean;
    /** Built in control id of built in control. Maximum length is 64 characters. */
    builtInControlId: string;
    /** App-wide unique ID. Maximum length is 64 characters. */
    id: string;
    type: ControlsType;
    /** Short label of the control. Maximum length is 64 characters. */
    label: string;
    /** Whether the control is initially enabled. */
    enabled: boolean;
    icons: Icons[];
    supertip: Supertip;
    /** The ID of an execution-type action that handles this key combination. Maximum length is 64 characters. */
    actionId: string;
    items: Controls[];
  }

  export enum ControlsType {
    Button = "button",
    Menu = "menu",
  }

  export class Supertip implements ISupertip {
    /** Title of the super tip. Maximum length is 64 characters. */
    title!: string;
    /** Description of the super tip. Maximum length is 128 characters. */
    description!: string;

    constructor(data?: ISupertip) {
      if (data) {
        for (var property in data) {
          if (data.hasOwnProperty(property)) (<any>this)[property] = (<any>data)[property];
        }
      }
    }

    init(_data?: any) {
      if (_data) {
        this.title = _data["title"];
        this.description = _data["description"];
      }
    }

    static fromJS(data: any): Supertip {
      data = typeof data === "object" ? data : {};
      let result = new Supertip();
      result.init(data);
      return result;
    }

    toJSON(data?: any) {
      data = typeof data === "object" ? data : {};
      data["title"] = this.title;
      data["description"] = this.description;
      return data;
    }
  }

  export interface ISupertip {
    /** Title of the super tip. Maximum length is 64 characters. */
    title: string;
    /** Description of the super tip. Maximum length is 128 characters. */
    description: string;
  }
} //Mos
